import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useOffline } from '../context/OfflineContext';
import {
  LayoutDashboard, FileText, FilePlus, Users, Package,
  Truck, RotateCcw, CreditCard, Settings, LogOut, Menu, X,
  Sun, Moon, ChevronDown, Globe, ShoppingCart, Warehouse, UserCog, PieChart, Wallet, MoreHorizontal,
  RefreshCw, WifiOff, CloudOff, Building2, ShoppingBag, Keyboard, Receipt, FileSignature
} from 'lucide-react';
import OfflineBanner, { SyncPanel, OfflineBadge } from './OfflineBanner';
import { languages } from '../utils';
import i18n from '../i18n';
import VirtualKeyboard, { isLargeAndroidScreen } from './VirtualKeyboard';
import { isAndroid } from '../utils/printService';

/* ─── Desktop Sidebar Item ─── */
const NavItem = ({ to, icon: Icon, label, onClick, highlight }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
        isActive
          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200 dark:shadow-indigo-900'
          : highlight
          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
      }`
    }
  >
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

/* ─── Mobile Bottom Tab Item ─── */
const MobileTab = ({ to, icon: Icon, label, onClick }) => (
  <NavLink to={to} onClick={onClick}
    className={({ isActive }) =>
      `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-xs font-medium transition-colors ${
        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'
      }`
    }
  >
    {({ isActive }) => (
      <>
        <div className={`p-1.5 rounded-xl transition-all ${isActive ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}`}>
          <Icon size={20} />
        </div>
        <span className="leading-none">{label}</span>
      </>
    )}
  </NavLink>
);

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isOnline, pendingCount, syncStatus, triggerSync } = useOffline();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [syncPanelOpen, setSyncPanelOpen] = useState(false);

  /* ── Clavier virtuel — uniquement sur la page Caisse (/pos) ── */
  const isLargeAnd = useMemo(() => isLargeAndroidScreen(), []);
  const vkbPref = useMemo(() => {
    try { return !!JSON.parse(localStorage.getItem('print_prefs') || '{}').posVirtualKeyboard; } catch { return false; }
  }, []);
  const isPosPage = pathname === '/pos';
  const showKeyboardButton = useMemo(
    () => isAndroid() && (isLargeAnd || vkbPref) && isPosPage,
    [isLargeAnd, vkbPref, isPosPage]
  );
  const [showVkb, setShowVkb] = useState(false);

  useEffect(() => {
    if (!showKeyboardButton) {
      // Quitter la Caisse : fermer le clavier et rendre le clavier système aux autres pages
      setShowVkb(false);
      return;
    }
    // Sur la Caisse : bloquer le clavier système sur tous les inputs
    const patchInputs = () => {
      document.querySelectorAll('input:not([inputmode="none"]), textarea:not([inputmode="none"])').forEach(el => {
        el.setAttribute('inputmode', 'none');
      });
    };
    patchInputs();
    const observer = new MutationObserver(patchInputs);
    observer.observe(document.body, { childList: true, subtree: true });
    const onFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') setShowVkb(true);
    };
    // touchstart : fallback Android où focusin ne se déclenche pas sur inputmode=none
    const onTouch = (e) => {
      const el = e.target.closest('input, textarea');
      if (el) { el.focus(); setShowVkb(true); }
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('touchstart', onTouch, { passive: true });
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('touchstart', onTouch);
    };
  }, [showKeyboardButton]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const POS_BLOCKED_PERMS = new Set(['dashboard', 'invoices', 'quotes', 'deliveries', 'returns', 'lettresChange']);
  const isPosAccount = user?.userType === 'pos';

  const allNavItems = [
    { to: '/',               icon: LayoutDashboard, label: t('nav.dashboard'),  perm: 'dashboard' },
    { to: '/pos',            icon: ShoppingCart,    label: 'Caisse',            perm: 'pos',       highlight: true },
    { to: '/invoices',       icon: FileText,        label: t('nav.invoices'),   perm: 'invoices' },
    { to: '/quotes',         icon: FilePlus,        label: t('nav.quotes'),     perm: 'quotes' },
    { to: '/credits',        icon: CreditCard,      label: t('nav.credits'),    perm: 'invoices' },
    { to: '/clients',        icon: Users,           label: t('nav.clients'),    perm: 'clients' },
    { to: '/client-credits',    icon: Wallet,   label: t('nav.client_credits', 'Crédits clients'), perm: 'credits' },
    { to: '/products',          icon: Package,  label: t('nav.products'),                          perm: 'products' },
    { to: '/business-expenses', icon: Receipt,  label: t('nav.expenses', 'Dépenses'),              perm: 'expenses' },
    { to: '/deliveries',     icon: Truck,           label: t('nav.deliveries'), perm: 'deliveries' },
    { to: '/returns',        icon: RotateCcw,       label: t('nav.returns'),    perm: 'returns' },
    { to: '/letters-of-change', icon: FileSignature, label: t('nav.lettresChange', 'Lettres de change'), perm: 'lettresChange' },
    { to: '/pos-report',     icon: PieChart,        label: 'Reporting',         perm: 'posReport' },
    { to: '/stock',          icon: Warehouse,       label: 'Stock',             perm: 'stock' },
    { to: '/suppliers',       icon: Building2,    label: 'Fournisseurs',      perm: 'suppliers' },
    { to: '/purchase-orders', icon: ShoppingBag,  label: 'Bons de commande',  perm: 'purchaseOrders' },
    ...(!user?.isSubUser ? [{ to: '/users', icon: UserCog, label: 'Utilisateurs', perm: null }] : []),
  ].filter(item => {
    // Compte POS : masquer tableau de bord, factures, devis, livraisons, retours, avoirs
    if (isPosAccount && item.perm && POS_BLOCKED_PERMS.has(item.perm)) return false;
    if (!user?.isSubUser) return true;
    if (!item.perm) return false;
    return user.permissions?.[item.perm] === true;
  });

  // Mobile bottom bar: show 4 most important + "Plus" button
  const mobileMainItems = allNavItems.slice(0, 4);
  const moreItems = allNavItems.slice(4);

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : 'w-64'}`}>
      <NavLink to="/" className="flex items-center gap-3 px-4 py-4 border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
        <img src="/logo-easyclick.png" alt="EasyClick" className="w-10 h-10 object-contain rounded-lg"/>
        <div>
          <div className="font-bold text-slate-900 dark:text-white text-sm leading-tight">{isPosAccount ? 'EasyVente' : 'EasyFacture'}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">by EasyClick</div>
        </div>
        {mobile && (
          <button onClick={(e) => { e.preventDefault(); setSidebarOpen(false); }} className="ml-auto text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        )}
      </NavLink>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {allNavItems.map(item => (
          <NavItem key={item.to} {...item} onClick={mobile ? () => setSidebarOpen(false) : undefined} />
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-slate-200 dark:border-slate-700 space-y-1">
        <NavItem to="/settings" icon={Settings} label={t('nav.settings')} onClick={mobile ? () => setSidebarOpen(false) : undefined} />
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors">
          <LogOut size={18} /><span>{t('nav.logout')}</span>
        </button>
      </div>
      <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900 overflow-hidden" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex-shrink-0">
        <Sidebar />
      </aside>

      {/* ── Mobile Slide-in Sidebar (all items) ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-72 h-full bg-white dark:bg-slate-900 shadow-2xl">
            <Sidebar mobile />
          </aside>
        </div>
      )}

      {/* ── Mobile "Plus" menu overlay ── */}
      {moreMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMoreMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-20 left-0 right-0 mx-3 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-3 grid grid-cols-3 gap-2"
            onClick={e => e.stopPropagation()}>
            {moreItems.map(item => (
              <NavLink key={item.to} to={item.to} onClick={() => setMoreMenuOpen(false)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-medium transition-colors ${
                    isActive ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`
                }>
                <item.icon size={20} />
                <span className="text-center leading-tight">{item.label}</span>
              </NavLink>
            ))}
            <NavLink to="/settings" onClick={() => setMoreMenuOpen(false)}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-medium transition-colors ${
                  isActive ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`
              }>
              <Settings size={20} />
              <span>Paramètres</span>
            </NavLink>
            <button onClick={() => { setMoreMenuOpen(false); handleLogout(); }}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <LogOut size={20} />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top bar */}
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          {/* Mobile hamburger (hidden on desktop — desktop has sidebar) */}
          <button onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <Menu size={22} />
          </button>

          {/* App name on mobile */}
          <span className="lg:hidden font-bold text-slate-800 dark:text-white text-base flex-1">{isPosAccount ? 'EasyVente' : 'EasyFacture'}</span>
          <div className="hidden lg:block flex-1" />

          {/* Language */}
          <div className="relative">
            <button onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <Globe size={16} />
              <span className="hidden sm:inline">{languages.find(l => l.code === i18n.language)?.flag}</span>
              <ChevronDown size={13} />
            </button>
            {langMenuOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50">
                {languages.map(lang => (
                  <button key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); localStorage.setItem('language', lang.code); setLangMenuOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                      i18n.language === lang.code ? 'text-indigo-600 font-medium' : 'text-slate-700 dark:text-slate-300'
                    }`}>
                    <span>{lang.flag}</span><span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clavier virtuel — visible si Android + grand écran ou activé dans Paramètres */}
          {showKeyboardButton && (
            <button
              onClick={() => setShowVkb(v => !v)}
              title="Clavier visuel"
              className={`p-2 rounded-lg transition-colors ${
                showVkb
                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}>
              <Keyboard size={18} />
            </button>
          )}

          {/* Theme */}
          <button onClick={toggleTheme}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Offline / Sync button */}
          <button
            onClick={() => isOnline && pendingCount > 0 ? triggerSync('full') : setSyncPanelOpen(true)}
            className={`relative p-2 rounded-lg transition-colors ${
              !isOnline
                ? 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                : pendingCount > 0
                ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title={!isOnline ? 'Mode hors-ligne' : pendingCount > 0 ? `${pendingCount} en attente` : 'Synchronisé'}
          >
            {!isOnline ? (
              <WifiOff size={18} />
            ) : syncStatus === 'syncing' ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : pendingCount > 0 ? (
              <>
                <CloudOff size={18} />
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              </>
            ) : (
              <RefreshCw size={18} />
            )}
          </button>
        </header>

        {/* Offline Banner */}
        <OfflineBanner />

        {/* Sync Panel */}
        {syncPanelOpen && <SyncPanel onClose={() => setSyncPanelOpen(false)} />}

        {/* Demo banner */}
        {user?.accountType === 'demo' && user?.demoExpiresAt && (() => {
          const daysLeft = Math.ceil((new Date(user.demoExpiresAt) - new Date()) / 86400000);
          const expired = daysLeft <= 0;
          return (
            <div className={`px-4 py-2 flex items-center justify-between text-sm flex-shrink-0 ${
              expired ? 'bg-red-600 text-white' : daysLeft <= 2 ? 'bg-amber-500 text-white' : 'bg-purple-600 text-white'
            }`}>
              <span className="font-medium text-xs sm:text-sm">
                {expired ? '⏱ Démo expirée' : `🧪 Démo — ${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`}
              </span>
              <a href="/#/demo" className="flex-shrink-0 bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded-lg font-semibold text-xs transition">
                🚀 Production
              </a>
            </div>
          );
        })()}

        {/* Page content — padding-bottom on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0 flex flex-col">
          <Outlet />
        </main>
      </div>

      {/* ══ CLAVIER VIRTUEL GLOBAL ══ */}
      {showVkb && <VirtualKeyboard onClose={() => setShowVkb(false)} />}

      {/* ══ MOBILE BOTTOM NAV BAR ══ */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {mobileMainItems.map(item => (
          <MobileTab key={item.to} to={item.to} icon={item.icon} label={item.label} />
        ))}
        {/* "Plus" button */}
        <button onClick={() => setMoreMenuOpen(o => !o)}
          className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-xs font-medium transition-colors ${
            moreMenuOpen ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'
          }`}>
          <div className={`p-1.5 rounded-xl transition-all ${moreMenuOpen ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}`}>
            <MoreHorizontal size={20} />
          </div>
          <span className="leading-none">Plus</span>
        </button>
      </nav>
    </div>
  );
}